<script setup lang="ts">
// Structured add/remove rows for the tool slots a task version declares, with a JSON toggle for power users. Every
// field is the kit's, as in TaskInputsEditor: it states no border, fill or radius of its own.
//
// A slot is a key the task's tools are named by (`codehost`, `codehost.create_issue`), a label a person choosing a
// connection reads, and an optional description. The key is checked with the shared rule when the slot is added and
// is never changed on a row after, since the tools name it: to rename one, remove it and add it again.

import {
    MAX_TOOL_SLOT_DESCRIPTION,
    MAX_TOOL_SLOT_LABEL,
    MAX_TOOL_SLOTS,
    type ToolSlot,
    type ToolSlotAction,
    toolSlotKeyProblem,
} from '@zerohuman/shared'
import { Button, Input, ZhFieldError, ZhPanel } from '@zerohuman/ui'
import { ref } from 'vue'
import RowsJsonSection from '@/components/RowsJsonSection.vue'
import { useRowsModel } from '@/composables/useRowsModel'
import {
    actionCountIssue,
    actionDescriptionIssue,
    actionNameIssue,
    type SlotActionRefusal,
} from '@/lib/toolSlotActions'

/** An action as its fields hold it: a description is always text here, and empty is stored as none. */
type ActionRow = { name: string; description: string }

/** A row as the fields hold it: a description is always text here, and empty is stored as none. */
type SlotRow = { key: string; label: string; description: string; actions: ActionRow[] }

const props = defineProps<{
    modelValue: ToolSlot[]
    /** #957: the API's refusal of a slot's actions on the last save, shown on the action or slot it names. */
    refusal?: SlotActionRefusal | null
}>()
const emit = defineEmits<{ 'update:modelValue': [ToolSlot[]] }>()

/**
 * The action rows of a slot as it came in, which the JSON toggle passes unchecked: a list that is no list is none, and
 * an action without a text name or description gets an empty one, so the row rules say what is wrong and the page
 * sends nothing, instead of the editor failing to draw.
 */
function actionRows(actions: unknown): ActionRow[] {
    if (!Array.isArray(actions)) return []
    return actions.map((each: unknown) => {
        const action = (each && typeof each === 'object' ? each : {}) as Partial<Record<keyof ToolSlotAction, unknown>>
        return {
            name: typeof action.name === 'string' ? action.name : '',
            description: typeof action.description === 'string' ? action.description : '',
        }
    })
}

/** An action as the task stores it: no description when it is blank. */
function actionValue(action: ActionRow): ToolSlotAction {
    const description = action.description.trim()
    return description ? { name: action.name.trim(), description } : { name: action.name.trim() }
}

const { rows, showJson, jsonText, jsonError, append, removeAt, toggleJson } = useRowsModel<SlotRow, ToolSlot>({
    modelValue: () => props.modelValue,
    emit: value => emit('update:modelValue', value),
    toRow: slot => ({
        key: slot.key,
        label: slot.label,
        description: slot.description ?? '',
        actions: actionRows(slot.actions),
    }),
    // A slot with no description, or no actions, is stored without them, so a blank field and a removed one are the
    // same shape.
    toValue: row => {
        const description = row.description.trim()
        const actions = row.actions.map(actionValue)
        return {
            key: row.key,
            label: row.label,
            ...(description ? { description } : {}),
            ...(actions.length ? { actions } : {}),
        }
    },
})

const draft = ref<Omit<SlotRow, 'actions'>>(emptyDraft())

/** Why the last add was refused, shown beside the add row until a slot is added. */
const addError = ref<string | null>(null)

function emptyDraft(): Omit<SlotRow, 'actions'> {
    return { key: '', label: '', description: '' }
}

/**
 * #957: the actions a slot declares, which a connection filling it maps to its own tools. Each slot has an add row of
 * its own (a name and an optional description), checked with the shared rules before it is added; an action already
 * listed is checked as it is edited, and the page sends nothing while one breaks a rule.
 */
const actionDrafts = ref<Record<string, ActionRow>>({})
const actionAddErrors = ref<Record<string, string>>({})

function actionDraft(key: string): ActionRow {
    return actionDrafts.value[key] ?? { name: '', description: '' }
}

function setActionDraft(key: string, field: keyof ActionRow, value: string | number): void {
    actionDrafts.value = { ...actionDrafts.value, [key]: { ...actionDraft(key), [field]: String(value) } }
}

function addAction(row: SlotRow): void {
    const typed = actionDraft(row.key)
    const name = typed.name.trim()
    const issue =
        actionCountIssue(row.actions.length) ??
        actionNameIssue(
            name,
            row.actions.map(action => action.name),
        ) ??
        actionDescriptionIssue(typed.description)
    if (issue) {
        actionAddErrors.value = { ...actionAddErrors.value, [row.key]: issue }
        return
    }
    row.actions.push({ name, description: typed.description.trim() })
    const { [row.key]: _added, ...otherErrors } = actionAddErrors.value
    actionAddErrors.value = otherErrors
    const { [row.key]: _typed, ...otherDrafts } = actionDrafts.value
    actionDrafts.value = otherDrafts
}

/** Why an action already listed cannot be saved as it stands: the shared rules, then the API's refusal of it. */
function actionIssue(row: SlotRow, index: number): string | null {
    const action = row.actions[index]
    if (!action) return null
    const others = row.actions.filter((_other, at) => at !== index).map(other => other.name)
    const own = actionNameIssue(action.name, others) ?? actionDescriptionIssue(action.description)
    if (own) return own
    const refused = props.refusal
    return refused && refused.key === row.key && refused.action === action.name.trim() ? refused.message : null
}

/** The API's refusal of a slot's whole list of actions, when it named this slot. */
function slotActionsIssue(row: SlotRow): string | null {
    const refused = props.refusal
    return refused && refused.key === row.key && refused.action === null ? refused.message : null
}

/** Why a slot key cannot be added, in words, or null when it can. The server applies the same rules on save. */
function keyIssue(key: string): string | null {
    const problem = toolSlotKeyProblem(key)
    if (problem === 'tool_slot_key_invalid') {
        return 'A slot key starts with a lowercase letter, then up to 31 lowercase letters, digits, - or _.'
    }
    if (problem === 'tool_slot_key_reserved') {
        return `"${key}" is the OS's own and cannot be a slot.`
    }
    if (rows.value.some(row => row.key === key)) {
        return 'This task already has a slot with that key.'
    }
    return null
}

/** A row's label, checked as the server will: required, and at most the shared length. */
function labelIssue(label: string): string | null {
    if (!label.trim()) return 'Give the slot a label.'
    if (label.trim().length > MAX_TOOL_SLOT_LABEL) return `Keep the label to ${MAX_TOOL_SLOT_LABEL} characters.`
    return null
}

function descriptionIssue(description: string): string | null {
    return description.trim().length > MAX_TOOL_SLOT_DESCRIPTION
        ? `Keep the description to ${MAX_TOOL_SLOT_DESCRIPTION} characters.`
        : null
}

function add(): void {
    if (rows.value.length >= MAX_TOOL_SLOTS) {
        addError.value = `A task declares at most ${MAX_TOOL_SLOTS} slots.`
        return
    }
    const key = draft.value.key.trim()
    const issue = keyIssue(key) ?? labelIssue(draft.value.label) ?? descriptionIssue(draft.value.description)
    if (issue) {
        addError.value = issue
        return
    }
    append({ key, label: draft.value.label.trim(), description: draft.value.description, actions: [] })
    addError.value = null
    draft.value = emptyDraft()
}
</script>

<template>
	<RowsJsonSection
		testid="task-tool-slots"
		title="Tool slots"
		:show-json="showJson"
		:json-text="jsonText"
		:json-error="jsonError"
		:json-rows="5"
		@update:json-text="jsonText = $event"
		@toggle="toggleJson"
	>
		<template #description>
			Name this task's tools by a slot key instead of one vendor's tool type (for example
			<code>codehost.create_issue</code>). Each company fills the slot with one of its own connections on the Tools
			page, so the task works whichever tool they connected.
		</template>
		<p v-if="!rows.length" class="text-muted-foreground text-sm">No tool slots.</p>
		<ul class="space-y-2">
			<ZhPanel
				v-for="(row, index) in rows"
				:key="index"
				as="li"
				data-testid="task-tool-slot-row"
				class="flex flex-wrap items-start gap-2"
			>
				<div class="grid gap-1 text-xs">
					Key
					<code class="py-2 font-mono text-sm" data-testid="task-tool-slot-key">{{ row.key }}</code>
				</div>
				<label class="grid gap-1 text-xs">
					Label
					<Input v-model="row.label" data-testid="task-tool-slot-label" :maxlength="MAX_TOOL_SLOT_LABEL" />
					<span v-if="labelIssue(row.label)" role="alert" class="text-destructive">{{ labelIssue(row.label) }}</span>
				</label>
				<label class="grid min-w-48 flex-1 gap-1 text-xs">
					Description
					<Input v-model="row.description" :maxlength="MAX_TOOL_SLOT_DESCRIPTION" />
				</label>
				<Button
					type="button"
					size="sm"
					variant="destructive"
					class="self-end"
					data-testid="task-tool-slot-remove"
					@click="removeAt(index)"
				>
					Remove
				</Button>
				<div class="grid w-full gap-2" data-testid="task-tool-slot-actions">
					<p class="text-muted-foreground text-xs">
						Actions: what the task needs any connection filling this slot to do. Each connection says which of
						its tools each one is.
					</p>
					<div
						v-for="(action, actionIndex) in row.actions"
						:key="actionIndex"
						class="flex flex-wrap items-start gap-2"
						data-testid="task-tool-slot-action-row"
					>
						<Input
							v-model="action.name"
							class="w-48"
							:aria-label="`Action ${actionIndex + 1} of ${row.key}`"
							data-testid="task-tool-slot-action-name"
						/>
						<Input
							v-model="action.description"
							class="min-w-48 flex-1"
							:aria-label="`What ${action.name || 'this action'} does`"
							placeholder="What it does (optional)"
							data-testid="task-tool-slot-action-description"
						/>
						<Button
							type="button"
							size="sm"
							variant="outline"
							:aria-label="`Remove ${action.name || 'this action'}`"
							data-testid="task-tool-slot-action-remove"
							@click="row.actions.splice(actionIndex, 1)"
						>
							Remove
						</Button>
						<ZhFieldError
							v-if="actionIssue(row, actionIndex)"
							:id="`task-tool-slot-action-error-${index}-${actionIndex}`"
							class="w-full"
							:message="actionIssue(row, actionIndex) ?? undefined"
							data-testid="task-tool-slot-action-error"
						/>
					</div>
					<div class="flex flex-wrap items-start gap-2">
						<Input
							:model-value="actionDraft(row.key).name"
							class="w-48"
							:aria-label="`New action of ${row.key}`"
							placeholder="Action name"
							data-testid="task-tool-slot-action-add-name"
							@update:model-value="setActionDraft(row.key, 'name', $event)"
						/>
						<Input
							:model-value="actionDraft(row.key).description"
							class="min-w-48 flex-1"
							:aria-label="`What the new action of ${row.key} does`"
							placeholder="What it does (optional)"
							data-testid="task-tool-slot-action-add-description"
							@update:model-value="setActionDraft(row.key, 'description', $event)"
						/>
						<Button
							type="button"
							size="sm"
							variant="outline"
							data-testid="task-tool-slot-action-add"
							@click="addAction(row)"
						>
							Add action
						</Button>
					</div>
					<ZhFieldError
						v-if="actionAddErrors[row.key]"
						:id="`task-tool-slot-action-add-error-${index}`"
						:message="actionAddErrors[row.key]"
						data-testid="task-tool-slot-action-add-error"
					/>
					<ZhFieldError
						v-if="slotActionsIssue(row)"
						:id="`task-tool-slot-actions-error-${index}`"
						:message="slotActionsIssue(row) ?? undefined"
						data-testid="task-tool-slot-actions-error"
					/>
				</div>
			</ZhPanel>
		</ul>
		<div class="flex flex-wrap items-end gap-2">
			<label class="grid gap-1 text-xs">
				Key
				<Input v-model="draft.key" data-testid="task-tool-slot-add-key" placeholder="codehost" />
			</label>
			<label class="grid gap-1 text-xs">
				Label
				<Input v-model="draft.label" data-testid="task-tool-slot-add-label" placeholder="Code host" />
			</label>
			<label class="grid min-w-48 flex-1 gap-1 text-xs">
				Description
				<Input v-model="draft.description" data-testid="task-tool-slot-add-description" placeholder="Where the code lives" />
			</label>
			<Button type="button" size="sm" variant="outline" data-testid="task-tool-slot-add" @click="add">Add slot</Button>
		</div>
		<p v-if="addError" data-testid="task-tool-slot-add-error" role="alert" class="text-destructive text-xs">{{ addError }}</p>
	</RowsJsonSection>
</template>
